import {
  APIComponentAttributes,
  APISurveyAttributes,
  ComponentSurveyApprovalFields,
  getSurveyLabel,
} from '@ardoq/api-types';
import { ButtonGroup, PrimaryButton } from '@ardoq/button';
import {
  ButtonWithDropdown,
  DropdownItem,
  DropdownOptionType,
} from '@ardoq/dropdown-menu';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { markdownToTextPreview } from '@ardoq/markdown';
import { WithPopover } from '@ardoq/popovers';
import {
  ChangeRequestIcon,
  DEFAULT_CHANGE_REQUEST_TOOLTIP,
} from './ChangeRequestIcon';

// View-layer type for surveys rendered by this package. Approval status is only
// populated for callers that fetch via fetchComponentSurveyApprovalStatuses;
// callers using fetchComponentSurveys still pass plain surveys.
export type ComponentSurveyWithOptionalApprovalStatus = APISurveyAttributes &
  Partial<ComponentSurveyApprovalFields>;

export const getNonPrioritySurveys = (
  prioritySurvey?: ComponentSurveyWithOptionalApprovalStatus,
  surveys?: ComponentSurveyWithOptionalApprovalStatus[]
): ComponentSurveyWithOptionalApprovalStatus[] =>
  surveys?.filter(survey => survey._id !== prioritySurvey?._id) ?? [];

export const getPrioritySurvey = (
  component: APIComponentAttributes,
  surveys?: ComponentSurveyWithOptionalApprovalStatus[]
): ComponentSurveyWithOptionalApprovalStatus | undefined =>
  surveys?.find(
    survey =>
      survey.discoverPriority && survey.componentTypeName === component.type
  );

export const getSurveyOptions = (
  surveys?: ComponentSurveyWithOptionalApprovalStatus[],
  onSurveyClick?: (survey: ComponentSurveyWithOptionalApprovalStatus) => void,
  changeRequestTooltip: string = DEFAULT_CHANGE_REQUEST_TOOLTIP
): DropdownItem[] => {
  if (!surveys?.length) return [];
  return surveys.map(survey => {
    return {
      type: DropdownOptionType.OPTION,
      label: getSurveyLabel(survey),
      description: survey.description
        ? markdownToTextPreview(survey.description)
        : undefined,
      truncateTitle: true,
      truncateDescription: true,
      ...(survey.changeRequest && {
        leftContent: <ChangeRequestIcon tooltip={changeRequestTooltip} />,
      }),
      onClick: () => onSurveyClick?.(survey),
    };
  });
};

type SurveyButtonsProps = {
  component: APIComponentAttributes;
  surveys?: ComponentSurveyWithOptionalApprovalStatus[];
  onSurveyClick?: (survey: ComponentSurveyWithOptionalApprovalStatus) => void;
  submitUpdateLabel?: string;
  // Localized tooltip + accessible label rendered on the change-request icon.
  // Defaults to English; callers with i18n context should pass a translated string.
  changeRequestTooltip?: string;
  dataTourId?: string;
  disabled?: boolean;
  disabledTooltip?: string;
};

const SurveyButtons = ({
  component,
  surveys,
  onSurveyClick,
  submitUpdateLabel = 'Submit an update',
  changeRequestTooltip = DEFAULT_CHANGE_REQUEST_TOOLTIP,
  dataTourId,
  disabled = false,
  disabledTooltip,
}: SurveyButtonsProps) => {
  const prioritySurvey = getPrioritySurvey(component, surveys);
  const otherSurveys = getNonPrioritySurveys(prioritySurvey, surveys);
  const options = getSurveyOptions(
    otherSurveys,
    onSurveyClick,
    changeRequestTooltip
  );
  return surveys?.length ? (
    <ButtonGroup dataTourId={dataTourId}>
      {otherSurveys.length ? (
        <ButtonWithDropdown
          dropdownSize={DropdownSize.AUTO}
          maxDropdownSize={DropdownSize.M}
          alignMenuTo="left"
          dataTestId="other-surveys-dropdown"
          options={options}
          isDisabled={disabled}
        >
          {submitUpdateLabel}
        </ButtonWithDropdown>
      ) : null}
      {prioritySurvey && (
        // Wraps only PrimaryButton, not the surrounding ButtonGroup: ButtonGroup
        // does not forward ref/hover handlers, so WithPopover has nothing to anchor to.
        <WithPopover content={disabled ? disabledTooltip : undefined}>
          <PrimaryButton
            onClick={() => onSurveyClick?.(prioritySurvey)}
            dataTestId="priority-survey-button"
            isDisabled={disabled}
          >
            {prioritySurvey.changeRequest && !disabled ? (
              <ChangeRequestIcon tooltip={changeRequestTooltip} />
            ) : null}
            {getSurveyLabel(prioritySurvey)}
          </PrimaryButton>
        </WithPopover>
      )}
    </ButtonGroup>
  ) : null;
};

export { SurveyButtons };
